import React, { useState, useEffect, useRef } from 'react';
import MediaGalleryDesigner from '../MediaGalleryDesigner';
import VariantDesigner from '../VariantDesigner';
import './ProductDesigner.css';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Inventory as InventoryIcon,
  Description as DescriptionIcon,
  Tag as TagIcon,
  Category as CategoryIcon,
  Close as CloseIcon,
  Link as LinkIcon,
  Add as AddIcon,
  LocalOffer as LocalOfferIcon,
  PhotoLibrary as MediaIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import Modal from '../Modal';
import ProductService from '../../services/ProductService';
import { useParams, useNavigate } from 'react-router-dom';
import CategoryService from '../../services/CategoryService';
import {
  getMediaDisplayUrl,
  normalizeProductMedia,
} from '../../utils/mediaUtils';
import { prepareProductMediaForSave } from '../../utils/mediaPrepare';
import VideoThumbnail from '../VideoThumbnail';

const PURCHASE_PLATFORMS = [
  { id: 'etsy', label: 'Etsy' },
  { id: 'shopify', label: 'Shopify' },
  { id: 'website', label: 'Site du créateur' },
  { id: 'other', label: 'Autre plateforme' },
];

const emptyPurchaseSlots = () => [
  { id: 'etsy', url: '' },
  { id: 'website', url: '' },
];

const purchaseSlotsFromProduct = (data = {}) => {
  const links = Array.isArray(data.purchaseLinks) ? data.purchaseLinks : [];
  const first =
    links[0] ||
    (data.externalLink ? { id: 'other', url: data.externalLink } : { id: 'etsy', url: '' });
  const second = links[1] || { id: 'website', url: '' };

  return [
    { id: first.id || 'other', url: first.url || '' },
    { id: second.id || 'website', url: second.url || '' },
  ];
};

const purchaseSlotsForApi = (slots) =>
  slots
    .filter((slot) => typeof slot.url === 'string' && slot.url.trim() !== '')
    .map((slot) => ({
      id: slot.id || 'other',
      url: slot.url.trim(),
    }));

const ProductDesigner = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState({
    name: '',
    reference: '',
    description: '',
    status: 'brouillon', // Par défaut: brouillon
    categories: [],
    purchaseLinks: emptyPurchaseSlots(),
    price: '',
    tags: [], // Nouveau champ pour les tags
    variants: [], // Nouveau champ pour les déclinaisons
    media: {
      photos: [],
      videos: [],
    },
  });

  const [newTag, setNewTag] = useState('');
  const MAX_TAGS = 15;

  // Simuler une liste de catégories disponibles
  // Dans une application réelle, ces données viendraient d'une API
  const [availableCategories, setAvailableCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);

  const [showMediaModal, setShowMediaModal] = useState(false);
  const [mediaViewMode, setMediaViewMode] = useState('gallery');
  const mediaGalleryRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Charger les catégories au montage du composant
  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    setIsLoadingCategories(true);
    try {
      const categories = await CategoryService.getCategories();
      setAvailableCategories(categories);
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  // Charger les données du produit si on est en mode édition
  useEffect(() => {
    if (id && id !== 'new') {
      setIsEditing(true);
      loadProductData(id);
    } else {
      setIsEditing(false);
      // Réinitialiser le formulaire pour un nouveau produit
      setProduct({
        name: '',
        reference: '',
        description: '',
        status: 'brouillon',
        categories: [],
        purchaseLinks: emptyPurchaseSlots(),
        price: '',
        tags: [],
        variants: [],
        media: {
          photos: [],
          videos: [],
        },
      });
    }
  }, [id]);

  const loadProductData = async (productId) => {
    setIsLoading(true);
    try {
      const productData = await ProductService.getProductById(productId);

      // Transformer les données pour correspondre à la structure attendue
      const formattedProduct = {
        ...productData,
        purchaseLinks: purchaseSlotsFromProduct(productData),
        categories: productData.categories ? productData.categories.map((cat) => cat.id) : [],
        tags: productData.tags || [],
        variants: productData.variants || [],
        media: normalizeProductMedia(productData.media),
      };

      setProduct(formattedProduct);
    } catch (error) {
      console.error('Erreur lors du chargement du produit:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProduct({
      ...product,
      [name]: value,
    });
  };

  const handleImagesChange = (newImages) => {
    setProduct({
      ...product,
      media: {
        ...product.media,
        photos: newImages,
      },
    });
  };

  const handleVideosChange = (newVideos) => {
    setProduct({
      ...product,
      media: {
        ...product.media,
        videos: newVideos,
      },
    });
  };

  const handleStatusChange = (e) => {
    setProduct({
      ...product,
      status: e.target.value,
    });
  };

  // Gestion des catégories
  const handleCategoryChange = (categoryId) => {
    const isSelected = product.categories.includes(categoryId);

    if (isSelected) {
      // Retirer la catégorie si elle est déjà sélectionnée
      setProduct({
        ...product,
        categories: product.categories.filter((category) => category !== categoryId),
      });
    } else {
      // Ajouter la catégorie si elle n'est pas déjà sélectionnée
      setProduct({
        ...product,
        categories: [...product.categories, categoryId],
      });
    }
  };

  // Gestion des tags
  const handleTagInputChange = (e) => {
    setNewTag(e.target.value);
  };

  const addTag = (e) => {
    e.preventDefault();
    if (newTag.trim() && product.tags.length < MAX_TAGS && !product.tags.includes(newTag.trim())) {
      setProduct({
        ...product,
        tags: [...product.tags, newTag.trim()],
      });
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setProduct({
      ...product,
      tags: product.tags.filter((tag) => tag !== tagToRemove),
    });
  };

  const handlePurchaseLinkChange = (index, field, value) => {
    const slots = Array.isArray(product.purchaseLinks)
      ? product.purchaseLinks
      : emptyPurchaseSlots();
    setProduct({
      ...product,
      purchaseLinks: slots.map((slot, slotIndex) =>
        slotIndex === index ? { ...slot, [field]: value } : slot
      ),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const media = await prepareProductMediaForSave(product.media);

      const productData = {
        name: product.name,
        reference: product.reference,
        description: product.description,
        status: product.status,
        categories: product.categories,
        purchaseLinks: purchaseSlotsForApi(product.purchaseLinks),
        price: product.price,
        tags: product.tags,
        variants: product.variants,
        media,
      };

      if (isEditing) {
        // Mettre à jour un produit existant
        await ProductService.updateProduct(id, productData);
      } else {
        // Créer un nouveau produit
        await ProductService.createProduct(productData);
      }

      // Rediriger vers la liste des produits après sauvegarde
      navigate('/products');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du produit:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const openMediaModal = () => {
    setShowMediaModal(true);
  };

  const closeMediaModal = () => {
    setShowMediaModal(false);
    setMediaViewMode('gallery');
  };

  const handleBackToMediaGallery = () => {
    mediaGalleryRef.current?.backToGallery();
  };

  // Afficher un aperçu des médias
  const renderMediaPreview = () => {
    const totalMedia = product.media.photos.length + product.media.videos.length;

    if (totalMedia === 0) {
      return <p className="text-gray-500 italic">Aucun média ajouté</p>;
    }

    return (
      <div className="media-preview">
        <div className="flex flex-wrap gap-2">
          {product.media.photos.map((photo, index) => (
            <div key={`preview-photo-${photo.id ?? photo.url ?? index}`} className="media-preview-thumb">
              <img
                src={getMediaDisplayUrl(photo)}
                alt={`Aperçu ${index + 1}`}
                className="media-preview-thumb-media"
                loading="lazy"
                decoding="async"
              />
            </div>
          ))}
          {product.media.videos.map((video, index) => (
            <div
              key={`preview-video-${video.id ?? video.src ?? video.url ?? index}`}
              className="media-preview-thumb media-preview-thumb--video"
            >
              <VideoThumbnail
                src={getMediaDisplayUrl(video)}
                className="media-preview-thumb-media"
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Ajouter un champ pour le prix
  const handlePriceChange = (e) => {
    const value = e.target.value;
    // Accepter uniquement les nombres et le point décimal
    if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setProduct({
        ...product,
        price: value,
      });
    }
  };

  const handleCancel = () => {
    navigate('/product-list');
  };

  // Gestion des déclinaisons
  const handleVariantsChange = (newVariants) => {
    setProduct({
      ...product,
      variants: newVariants,
    });
  };

  return (
    <div className="product-designer bg-white rounded-lg shadow-lg p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
        <InventoryIcon className="mr-2" />
        {isEditing ? 'Modification du produit' : "Création d'un nouveau produit"}
      </h2>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div className="form-group">
              <label htmlFor="name" className="flex items-center text-gray-700 font-medium">
                <TagIcon className="mr-2 text-blue-500" /> Nom du produit
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={product.name}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50"
              />
            </div>

            <div className="form-group">
              <label htmlFor="reference" className="flex items-center text-gray-700 font-medium">
                <CategoryIcon className="mr-2 text-blue-500" /> Référence
              </label>
              <input
                type="text"
                id="reference"
                name="reference"
                value={product.reference}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50"
              />
            </div>

            <div className="form-group">
              <p className="flex items-center text-gray-700 font-medium mb-1">
                <LinkIcon className="mr-2 text-blue-500" /> Liens d&apos;achat (optionnel)
              </p>
              <p className="mt-1 mb-4 text-sm text-gray-500">
                Deux emplacements pour indiquer où acheter cette pièce (Etsy, Shopify, votre
                site, autre marketplace). Ils apparaissent dans le panneau « Acheter cette
                pièce » de la fiche publique.
              </p>
              {(Array.isArray(product.purchaseLinks) ? product.purchaseLinks : emptyPurchaseSlots()).map(
                (slot, index) => (
                  <div
                    key={`purchase-link-${index}`}
                    className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4"
                  >
                    <p className="mb-3 text-sm font-semibold text-gray-800">
                      Lien {index + 1}
                    </p>
                    <label htmlFor={`purchase-platform-${index}`}>
                      Plateforme
                    </label>
                    <select
                      id={`purchase-platform-${index}`}
                      value={slot.id}
                      onChange={(e) => handlePurchaseLinkChange(index, 'id', e.target.value)}
                    >
                      {PURCHASE_PLATFORMS.map((platform) => (
                        <option key={platform.id} value={platform.id}>
                          {platform.label}
                        </option>
                      ))}
                    </select>
                    <label htmlFor={`purchase-url-${index}`}>
                      Adresse URL
                    </label>
                    <input
                      type="url"
                      id={`purchase-url-${index}`}
                      value={slot.url}
                      onChange={(e) => handlePurchaseLinkChange(index, 'url', e.target.value)}
                      placeholder={
                        index === 0
                          ? 'https://www.etsy.com/listing/123'
                          : 'https://www.exemple.com/produit'
                      }
                    />
                  </div>
                )
              )}
            </div>

            <div className="form-group">
              <label htmlFor="description" className="flex items-center text-gray-700 font-medium">
                <DescriptionIcon className="mr-2 text-blue-500" /> Description
              </label>
              <textarea
                id="description"
                name="description"
                value={product.description}
                onChange={handleInputChange}
                rows="4"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50"
              ></textarea>
            </div>

            <div className="form-group">
              <label className="flex items-center text-gray-700 font-medium mb-2">
                <LocalOfferIcon className="mr-2 text-blue-500" /> Prix
              </label>
              <input
                type="text"
                id="price"
                name="price"
                value={product.price}
                onChange={handlePriceChange}
                placeholder="0.00"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50"
              />
            </div>

            <div className="form-group">
              <label className="flex items-center text-gray-700 font-medium mb-2">
                <LocalOfferIcon className="mr-2 text-blue-500" /> Tags (SEO)
              </label>
              <div className="mb-2">
                <div className="flex flex-wrap gap-2 mb-3">
                  {product.tags.map((tag, index) => (
                    <div key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full flex items-center">
                      <span>{tag}</span>
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-1 text-blue-800 hover:text-blue-900 focus:outline-none"
                      >
                        <CloseIcon fontSize="small" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex">
                  <input
                    type="text"
                    value={newTag}
                    onChange={handleTagInputChange}
                    placeholder="Ajouter un tag"
                    className="flex-grow rounded-l-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50"
                    disabled={product.tags.length >= MAX_TAGS}
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className={`px-4 py-2 rounded-r-md flex items-center ${
                      product.tags.length >= MAX_TAGS
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                    disabled={product.tags.length >= MAX_TAGS}
                  >
                    <AddIcon fontSize="small" className="mr-1" /> Ajouter
                  </button>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  {product.tags.length}/{MAX_TAGS} tags - Ajoutez des mots-clés pour améliorer le référencement
                </p>
              </div>
            </div>
            <div className="form-group">
              <label className="text-gray-700 font-medium block mb-2">Statut</label>
              <div className="flex flex-wrap gap-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="status"
                    value="brouillon"
                    checked={product.status === 'brouillon'}
                    onChange={handleStatusChange}
                    className="form-radio h-5 w-5 text-blue-600"
                  />
                  <span className="ml-2 text-gray-700">Brouillon</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="status"
                    value="active"
                    checked={product.status === 'active'}
                    onChange={handleStatusChange}
                    className="form-radio h-5 w-5 text-blue-600"
                  />
                  <span className="ml-2 text-gray-700">Actif</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="status"
                    value="inactive"
                    checked={product.status === 'inactive'}
                    onChange={handleStatusChange}
                    className="form-radio h-5 w-5 text-blue-600"
                  />
                  <span className="ml-2 text-gray-700">Inactif</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="status"
                    value="archive"
                    checked={product.status === 'archive'}
                    onChange={handleStatusChange}
                    className="form-radio h-5 w-5 text-blue-600"
                  />
                  <span className="ml-2 text-gray-700">Archivé</span>
                </label>
              </div>
            </div>

            <div className="form-group">
              <label className="text-gray-700 font-medium block mb-2">
                <CategoryIcon className="mr-1" />
                Catégories
              </label>
              {isLoadingCategories ? (
                <div className="animate-pulse flex space-x-4">
                  <div className="h-6 bg-gray-200 rounded w-1/4"></div>
                  <div className="h-6 bg-gray-200 rounded w-1/4"></div>
                  <div className="h-6 bg-gray-200 rounded w-1/4"></div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {availableCategories.map((category) => (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => handleCategoryChange(category.id)}
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        product.categories.includes(category.id)
                          ? 'bg-blue-100 text-blue-800 border border-blue-300'
                          : 'bg-gray-100 text-gray-800 border border-gray-300 hover:bg-gray-200'
                      }`}
                    >
                      {category.name}
                      {product.categories.includes(category.id) && <CloseIcon fontSize="small" className="ml-1" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="text-gray-700 font-medium block mb-2">Médias</label>
            <div className="border border-gray-300 rounded-md p-4">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="font-medium">Galerie de médias</h3>
                  <p className="text-sm text-gray-500">
                    {product.media.photos.length} images, {product.media.videos.length} vidéos
                  </p>
                </div>
                <button
                  type="button"
                  onClick={openMediaModal}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  <MediaIcon className="mr-2" /> Gérer les médias
                </button>
              </div>

              {renderMediaPreview()}
            </div>
          </div>

          <div className="form-group">
            <VariantDesigner variants={product.variants} onVariantsChange={handleVariantsChange} basePrice={product.price} />
          </div>

          <div className="flex justify-end space-x-4 mt-8">
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
              disabled={isLoading}
              onClick={handleCancel}
            >
              <CancelIcon className="mr-2" /> Annuler
            </button>
            <button
              type="submit"
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              disabled={isLoading}
            >
              <SaveIcon className="mr-2" />
              {isLoading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      )}

      {/* Modal pour la gestion des médias */}
      {showMediaModal && (
        <Modal onClose={closeMediaModal} title="Gestion des médias">
          <div className="media-modal-layout">
            <div className="media-modal-scroll">
              <MediaGalleryDesigner
                ref={mediaGalleryRef}
                images={product.media.photos}
                videos={product.media.videos}
                maxImages={5}
                maxVideos={3}
                onImagesChange={handleImagesChange}
                onVideosChange={handleVideosChange}
                onViewModeChange={setMediaViewMode}
              />
            </div>
            <div className={`media-modal-actions ${mediaViewMode === 'detail' ? 'media-modal-actions--split' : ''}`}>
              {mediaViewMode === 'detail' && (
                <button
                  type="button"
                  onClick={handleBackToMediaGallery}
                  className="media-modal-back-button"
                >
                  <BackIcon fontSize="small" />
                  <span>Retour à la galerie</span>
                </button>
              )}
              <button
                type="button"
                onClick={closeMediaModal}
                className="media-modal-finish-button"
              >
                Terminer
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ProductDesigner;
